import { getCell, parsePastedAmount } from './parsePastedGrid';
import { parsePastedPeriods } from './parsePastedPeriods';
import type {
  ChildBandsCount,
  ExcelPasteIssue,
  PastedPeriod,
  PastedPersonPrices,
} from './types';

/**
 * Reads the tariff label and the 7 period lines that follow it, shared by all
 * blocks. The label is on line 1, or lower when the block starts with extra
 * lines (the pension types of a festive supplement).
 */
export function parseBlockHeader(
  rows: string[][],
  labelLine = 1,
): {
  name: string;
  periods: PastedPeriod[];
  errors: ExcelPasteIssue[];
} {
  const errors: ExcelPasteIssue[] = [];
  const name = getCell(rows, labelLine, 0);
  if (name === '') {
    errors.push({ code: 'missingLabel', params: { line: labelLine } });
  }

  const { periods, errors: periodErrors } = parsePastedPeriods(
    rows,
    labelLine + 1,
  );
  return { name, periods, errors: [...errors, ...periodErrors] };
}

/**
 * Reads one amount cell. Empty stays null: "no pasted price, no price".
 */
export function parseAmountCell(
  rows: string[][],
  line: number,
  column: number,
  errors: ExcelPasteIssue[],
): number | null {
  const raw = getCell(rows, line, column);
  const amount = parsePastedAmount(raw);
  switch (amount.kind) {
    case 'amount':
      return amount.value;
    case 'notAnAmount':
      errors.push({ code: 'notAnAmount', params: { line, value: raw } });
      return null;
    case 'invalid':
      errors.push({ code: 'invalidAmount', params: { line } });
      return null;
    default:
      return null;
  }
}

/**
 * Reads a per-person line: adult, older children band, younger children band.
 * With a single band, the grid puts it in the younger column (column 3), which
 * is `childPrice` (childAges[0]) in the form.
 */
export function parsePersonPricesLine(
  rows: string[][],
  line: number,
  childBandsCount: ChildBandsCount,
  errors: ExcelPasteIssue[],
): PastedPersonPrices {
  const adultPrice = parseAmountCell(rows, line, 0, errors);
  const adolescentPrice = parseAmountCell(rows, line, 1, errors);
  const childPrice = parseAmountCell(rows, line, 2, errors);

  if (adolescentPrice !== null && childBandsCount < 2) {
    errors.push({ code: 'missingChildBand', params: { line, band: 'older' } });
  }
  if (childPrice !== null && childBandsCount < 1) {
    errors.push({
      code: 'missingChildBand',
      params: { line, band: 'younger' },
    });
  }

  return { adultPrice, adolescentPrice, childPrice };
}

export const hasAnyPrice = ({
  adultPrice,
  adolescentPrice,
  childPrice,
}: PastedPersonPrices) =>
  adultPrice !== null || adolescentPrice !== null || childPrice !== null;
